/**
 * Engine Review Types — Phase 07
 *
 * TypeScript types for the accountant attestation engine review system.
 * Mirrors the backend Pydantic models from Phase 06.
 */

// =============================================================================
// ENGINE REVIEW STATE
// =============================================================================

/**
 * Possible states for an engine review.
 * See a5-state-machines.md SM1 for state machine transitions.
 */
export type EngineReviewState =
  | 'unreviewed'
  | 'under_review'
  | 'approved'
  | 'disputed'
  | 'stale_after_rerun'
  | 'rerun_pending'
  | 'waived';

/**
 * Engine IDs — generated from the backend DAG (engine_dag.py) so this union
 * can never drift from the backend again. Regenerate with
 * `python backend/scripts/codegen_frontend_engines.py`.
 */
import {
  ALL_ENGINE_IDS,
  GENERATED_ENGINE_DEPENDENCIES,
  GENERATED_ENGINE_NAMES,
  type EngineId,
} from './engines.generated';

export type { EngineId };
export { ALL_ENGINE_IDS };

// =============================================================================
// VERIFIER TYPES
// =============================================================================

/**
 * Individual verifier check result.
 */
export interface VerifierCheck {
  check_id: string;
  check_name: string;
  passed: boolean;
  severity: 'info' | 'warning' | 'blocker';
  message: string;
  details?: Record<string, unknown>;
}

/**
 * Verifier report for a single engine.
 */
export interface VerifierReport {
  // Optional: the backend VerifierReport schema omits these on some reports
  // (real engine-review responses can carry only checks_failed). Readers MUST
  // coalesce (`?? []`). Prod crash 2026-08-24 came from treating them as required.
  checks_passed?: VerifierCheck[];
  checks_failed?: VerifierCheck[];
  engagement_score?: number;
  verified_at: string;
}

// =============================================================================
// OVERRIDE TYPES
// =============================================================================

/**
 * An in-house accountant override on an engine field.
 */
export interface EngineOverride {
  override_id: string;
  field_id: string;
  original_value: string | number | null;
  override_value: string | number | null;
  note: string;
  accountant_id: string;
  accountant_name?: string;
  created_at: string;
  is_active: boolean;
}

// =============================================================================
// CONSENSUS TYPES
// =============================================================================

/**
 * Agent position from War Room consensus.
 */
export interface AgentPosition {
  agent_name: string;
  value: string | number | null;
  confidence: number | null;
  reasoning: string;
}

/**
 * Consensus entry showing how inputs were resolved.
 */
export interface ConsensusEntry {
  field_id: string;
  field_name: string;
  resolved_value: string | number | null;
  resolution_method: 'confidence_weighting' | 'majority' | 'human_override' | 'single_source';
  agent_positions: AgentPosition[];
  dissent?: {
    agent_name: string;
    preferred_value: string | number | null;
    reasoning: string;
  }[];
}

// =============================================================================
// ATTESTATION HISTORY
// =============================================================================

/**
 * Historical entry in the attestation trail.
 */
export interface AttestationHistoryEntry {
  action: 'approved' | 'disputed' | 'rerun_requested' | 'waived' | 'rerun_completed' | 'override_applied';
  actor_id: string;
  actor_name: string;
  actor_role: 'external_attester' | 'in_house_accountant' | 'system';
  note?: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

// =============================================================================
// UPSTREAM PROVENANCE
// =============================================================================

/**
 * Summary of an upstream engine's status.
 */
export interface UpstreamEngineSummary {
  engine_id: EngineId;
  engine_name: string;
  status: EngineReviewState;
  last_run_at?: string;
}

// =============================================================================
// ENGINE REVIEW STATUS (MAIN DTO)
// =============================================================================

/**
 * Complete review status for a single engine.
 * Returned by GET /engine-review/{report_id}/{engine_id}.
 */
export interface EngineReviewStatus {
  report_id: string;
  engine_id: EngineId;
  engine_name: string;
  status: EngineReviewState;
  reviewed_by?: string;
  reviewed_by_name?: string;
  reviewed_at?: string;
  verifier_status?: VerifierReport;
  consensus_entries?: ConsensusEntry[];
  overrides_count: number;
  active_overrides?: EngineOverride[];
  upstream_engines?: UpstreamEngineSummary[];
  attestation_history: AttestationHistoryEntry[];
  last_run_at?: string;
  output_snapshot_id?: string;
}

/**
 * List response for all engines on a report.
 */
export interface EngineReviewListResponse {
  report_id: string;
  engines: EngineReviewStatus[];
  all_approved: boolean;
  finalize_enabled: boolean;
  // Counts over the finalize-gating BI cascade only — NOT engines.length, which
  // includes property + internal engines. Use these for the "approved of N" badge.
  attestable_total: number;
  attestable_approved: number;
}

// =============================================================================
// ACTION PAYLOADS
// =============================================================================

/**
 * Payload for approving an engine.
 */
export interface ApprovePayload {
  note?: string;
  acknowledges_verifier_warnings?: boolean;
  /**
   * dual_method engine ONLY, out-of-tolerance: the accountant's court-facing variance
   * attribution (>=10 chars). Stored on the SM1 engine_reviews row on approve and gates
   * the carrier exhibit's "reasonableness check". Backend is accountant-only + invited.
   */
  variance_attribution?: string;
}

/**
 * Payload for disputing an engine.
 */
export interface DisputePayload {
  reason: string;
  disputed_fields: string[];
  severity: 'low' | 'medium' | 'high' | 'critical';
}

/**
 * Payload for requesting an engine rerun.
 */
export interface RerunPayload {
  reason: string;
  cascade_downstream?: boolean;
}

/**
 * Payload for waiving an engine.
 */
export interface WaivePayload {
  reason: string;
  override_warnings: string[];
  attorney_override_reference?: string;
}

/**
 * Payload for adding an override (in-house only).
 *
 * Mirrors backend `OverrideCreateRequest` in
 * `backend/app/api/v1/engine_override_api.py:41-49`. The `note` must be
 * at least 10 characters per backend validation; surface this constraint
 * client-side via `OverrideFormModal`'s required-length check.
 */
export interface OverridePayload {
  field_id: string;
  override_value: string | number | null;
  /** The pre-override value being replaced. Required by backend so the
   * audit chain pins both sides of the swap. */
  original_value: string | number | null;
  /** Reviewer note explaining the override. Backend rejects < 10 chars. */
  note: string;
  /** Default true on backend — set false to apply override without
   * cascading invalidation + downstream rerun. */
  auto_trigger_rerun?: boolean;
  /** Provenance of the override. `"manual"` (default) when the in-house
   * accountant authored the change directly; `"from_ai_suggestion"` when it
   * originated from a Forensic Assistant "prep the claim" proposal that the
   * human reviewed and attested. Backend `OverrideCreateRequest` accepts it. */
  origin?: 'manual' | 'from_ai_suggestion';
  /** When `origin === "from_ai_suggestion"`, the id of the AI audit record
   * (`ai_audit_id` from the `prepare_claim` tool result) that prompted this
   * override. Pins the override back to the AI proposal in the audit chain. */
  prompted_by_ai_audit_id?: string;
}

/**
 * A Forensic Assistant override proposal carried via `react-router` location
 * state from the accountant chat ("Review & apply") into the engine review
 * pages. The detail page consumes it to auto-open the {@link OverridePayload}
 * modal pre-filled and provenance-tagged. `suggestedValue` may be null — the
 * AI deliberately declines to invent dollar values; the human enters them.
 */
export interface OverrideSuggestion {
  engineId: EngineId;
  fieldId: string;
  fieldLabel: string;
  originalValue: string | number | null;
  suggestedValue: string | number | null;
  rationale: string;
  origin: 'from_ai_suggestion';
  promptedByAiAuditId: string;
}

/** Response shape of POST /engine-review/{id}/{engine}/override. */
export interface OverrideCreateResponse {
  success: boolean;
  override_id: string;
  superseded_override_id?: string | null;
  rerun_triggered: boolean;
  rerun_task_id?: string | null;
  downstream_engines_invalidated: string[];
}

/** Response shape of DELETE /engine-review/{id}/{engine}/override/{override_id}. */
export interface OverrideInactivateResponse {
  success: boolean;
  override_id: string;
  rerun_triggered: boolean;
  rerun_task_id?: string | null;
}

// =============================================================================
// UI HELPER TYPES
// =============================================================================

/**
 * Display configuration for each review state.
 */
export interface ReviewStateDisplay {
  state: EngineReviewState;
  label: string;
  color: 'gray' | 'blue' | 'green' | 'red' | 'yellow';
  icon: 'clock' | 'eye' | 'check' | 'x' | 'refresh' | 'alert' | 'skip';
  description: string;
}

/**
 * Props for the mode of the review panel.
 */
export type ReviewPanelMode = 'external' | 'in_house_readonly' | 'in_house_edit';

/**
 * Configuration for which actions are enabled per state.
 */
export interface ActionEnabledState {
  start_review: boolean;
  approve: boolean;
  dispute: boolean;
  rerun: boolean;
  waive: boolean;
  startReviewTooltip?: string;
  approveTooltip?: string;
  disputeTooltip?: string;
  rerunTooltip?: string;
  waiveTooltip?: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Human-readable engine names.
 */
export const ENGINE_NAMES: Record<EngineId, string> = {
  // Backend display names for every engine in the DAG; the overrides below
  // preserve the established UI labels for the original attestation set.
  ...GENERATED_ENGINE_NAMES,
  merchant_reconciliation: 'Merchant Reconciliation',
  merchant_revenue_augmentation: 'Revenue Augmentation',
  bottom_up: 'Bottom-Up Analysis',
  enhanced_claim: 'Enhanced Claim',
  dual_method: 'Dual Method Comparison',
  mitigation: 'Mitigation Analysis',
  impact: 'Impact Analysis',
  epi: 'Extra/Ordinary Period Income',
  policy_deductions: 'Policy Deductions',
  pv: 'Present Value',
  fair_settlement: 'Fair Settlement',
  case_strength: 'Case Strength',
  property: 'Property Valuation (RCV/ACV)',
};

export const ENGINE_DESCRIPTIONS: Record<EngineId, string> = {
  merchant_reconciliation: 'Reconciles merchant-reported revenue against bank and P&L sources',
  merchant_revenue_augmentation: 'Identifies additional revenue streams from merchant processor data',
  bottom_up: 'Reconstructs lost income from individual transaction records',
  enhanced_claim: 'Applies augmentation factors to the base loss calculation',
  dual_method: 'Compares bottom-up and enhanced claim methodologies for consistency',
  mitigation: 'Calculates mitigation offsets and avoided-loss deductions',
  impact: 'Analyzes the causal impact of the insured event on business operations',
  epi: 'Computes extra expense and extended period of indemnity amounts',
  policy_deductions: 'Applies policy-specific deductions, sublimits, and co-insurance',
  pv: 'Discounts future loss streams to present value',
  fair_settlement: 'Synthesizes all engines into a defensible settlement range',
  case_strength: 'Scores overall case strength and litigation readiness',
  property: 'First-party property valuation: RCV/ACV, recoverable depreciation, O&L, debris',
  projection_trend: 'Methodology-loop BI projection using trend analysis of pre-loss revenue',
  two_period: 'Methodology-loop BI calculation comparing pre-loss vs post-loss periods',
  capacity_ceiling: 'Detects operational capacity ceilings that cap the but-for revenue projection',
  non_recurring_item_detector: 'Flags non-recurring revenue/expense items that would distort the baseline',
  catch_up_sales: 'Offsets the loss for post-restoration catch-up sales vs the projection',
  monthly_limit_cap: 'Applies the CP 15 10 Monthly Limit of Indemnity cap per month',
  extra_expense_cap: 'Enforces the CP 00 30 three-way Extra Expense minimum cap',
  third_party_damages: 'Third-party demand from elected loss rows, fault, and the other party limit',
  tort_lost_profits: 'Deterministic tort lost-profits engine for third-party claims',
  l_recon: 'Allocates the gross third-party recovery between insurer and insured (subrogation)',
};

/**
 * Engine dependency DAG — mirrors `backend/app/services/engine_dag.py`.
 * Used by review UI to show upstream provenance + downstream blast radius
 * on a per-engine basis. Keep in sync with the backend source of truth.
 */
export const ENGINE_DEPENDENCIES: Record<EngineId, EngineId[]> =
  GENERATED_ENGINE_DEPENDENCIES;

/**
 * Reverse index: which engines consume this one's output.
 * Derived from ENGINE_DEPENDENCIES at module load.
 */
export const ENGINE_DOWNSTREAM: Record<EngineId, EngineId[]> = (() => {
  const downstream = Object.fromEntries(
    ALL_ENGINE_IDS.map((id) => [id, []]),
  ) as Record<EngineId, EngineId[]>;
  for (const [engine, upstreams] of Object.entries(ENGINE_DEPENDENCIES) as Array<[EngineId, EngineId[]]>) {
    for (const upstream of upstreams) {
      downstream[upstream].push(engine);
    }
  }
  return downstream;
})();

/**
 * Display configuration for all review states.
 */
export const REVIEW_STATE_DISPLAY: Record<EngineReviewState, ReviewStateDisplay> = {
  unreviewed: {
    state: 'unreviewed',
    label: 'Not yet reviewed',
    color: 'gray',
    icon: 'clock',
    description: 'This engine has not been reviewed yet.',
  },
  under_review: {
    state: 'under_review',
    label: 'In review',
    color: 'blue',
    icon: 'eye',
    description: 'This engine is currently being reviewed.',
  },
  approved: {
    state: 'approved',
    label: 'Approved',
    color: 'green',
    icon: 'check',
    description: 'This engine has been approved.',
  },
  disputed: {
    state: 'disputed',
    label: 'Disputed',
    color: 'red',
    icon: 'x',
    description: 'This engine has been disputed and is awaiting in-house review.',
  },
  stale_after_rerun: {
    state: 'stale_after_rerun',
    label: 'Upstream changed',
    color: 'yellow',
    icon: 'alert',
    description: 'An upstream engine was re-run. This engine needs re-review.',
  },
  rerun_pending: {
    state: 'rerun_pending',
    label: 'Rerun in progress',
    color: 'blue',
    icon: 'refresh',
    description: 'This engine is being re-run. Please wait.',
  },
  waived: {
    state: 'waived',
    label: 'Waived',
    color: 'gray',
    icon: 'skip',
    description: 'This engine has been waived.',
  },
};

/**
 * Get which actions are enabled for a given state.
 */
export function getActionsForState(state: EngineReviewState): ActionEnabledState {
  // SM1 (engine_review_state_service.py): start_review is the only legal
  // edge out of `unreviewed`. The other affordances unlock once the review
  // is engaged. Approve/dispute/waive directly from `unreviewed` is also
  // technically allowed by the backend, but UX-wise we route reviewers
  // through start-review first so the audit trail captures intent.
  switch (state) {
    case 'unreviewed':
      return {
        start_review: true,
        approve: false,
        dispute: false,
        rerun: false,
        waive: false,
        approveTooltip: 'Click "Start review" first to engage the audit trail',
        disputeTooltip: 'Click "Start review" first to engage the audit trail',
        waiveTooltip: 'Click "Start review" first to engage the audit trail',
      };
    case 'under_review':
      return { start_review: false, approve: true, dispute: true, rerun: false, waive: true };
    case 'approved':
      return {
        start_review: false,
        approve: false,
        dispute: true,
        rerun: false,
        waive: true,
        approveTooltip: 'Already approved',
      };
    case 'disputed':
      return {
        start_review: false,
        approve: false,
        dispute: false,
        rerun: false,
        waive: false,
        approveTooltip: 'Awaiting in-house resolution',
        disputeTooltip: 'Already disputed',
      };
    case 'stale_after_rerun':
      return {
        start_review: false,
        approve: false,
        dispute: true,
        rerun: true,
        waive: true,
        approveTooltip: 'Upstream changed — request rerun or review updated data',
      };
    case 'rerun_pending':
      return {
        start_review: false,
        approve: false,
        dispute: false,
        rerun: false,
        waive: false,
        approveTooltip: 'Rerun in progress',
        disputeTooltip: 'Rerun in progress',
        rerunTooltip: 'Rerun in progress',
        waiveTooltip: 'Rerun in progress',
      };
    case 'waived':
      return {
        start_review: false,
        approve: true,
        dispute: true,
        rerun: false,
        waive: false,
        waiveTooltip: 'Already waived',
      };
    default:
      return { start_review: false, approve: false, dispute: false, rerun: false, waive: false };
  }
}

// =============================================================================
// REPORT-LEVEL VERIFIER + EXTERNAL-ATTESTER FINALIZE
// =============================================================================

/**
 * A single check in the REPORT-LEVEL post-deliberation verifier report
 * (`GET /api/v1/war-room/verifier/{report_id}`). Distinct from the per-engine
 * {@link VerifierCheck}: report-level checks carry `check_name` / `severity` /
 * free-text `details` (no per-engine `check_id` / `message` / `passed`).
 */
export interface ReportVerifierCheck {
  check_name: string;
  severity: 'info' | 'warning' | 'blocker';
  details?: string;
  affected_engines?: string[] | null;
  affected_events?: string[] | null;
}

/**
 * The report-level verifier report. `run_id` is the token the attester must
 * echo back as `acknowledged_verifier_run_id` to finalize past an
 * acknowledgment-required WARNING (see {@link ACK_REQUIRED_VERIFIER_CHECKS}).
 */
export interface ReportVerifierReport {
  run_id: string;
  report_id: string;
  overall_status: 'passed' | 'warnings' | 'blocked';
  // Optional to match the API (see VerifierReport note). Readers coalesce.
  checks_passed?: ReportVerifierCheck[];
  checks_failed?: ReportVerifierCheck[];
  verified_at?: string;
  verifier_version?: string;
}

/**
 * Verifier WARNING `check_name`s the external attester MUST explicitly
 * acknowledge before finalize. Mirrors the backend
 * `_ACK_REQUIRED_VERIFIER_CHECKS` in `external_accountant.py`.
 * `endorsement_grounding` flags a possible fabricated/ungrounded coverage
 * assertion (CP 04 11 grounding hardening) — an existential-class defect that
 * must reach a human before a forward-facing deliverable ships.
 * `dual_method_tolerance` (G2b) flags a MATERIAL top-down/bottom-up BI divergence
 * — the "reconciled" sworn midpoint sits between two methods that disagree, so the
 * attester must see and acknowledge it (re-evaluated live at finalize).
 */
export const ACK_REQUIRED_VERIFIER_CHECKS = [
  'endorsement_grounding',
  'dual_method_tolerance',
] as const;

/** Request body for `POST /external-accountant/review/{report_id}/finalize`. */
export interface FinalizePayload {
  signature_confirmation: string;
  final_notes?: string;
  acknowledged_verifier_run_id?: string;
}

/** Response from the finalize endpoint. */
export interface FinalizeResponse {
  success: boolean;
  report_id: string;
  new_status: string;
  attested_at: string;
  attested_by: Record<string, unknown>;
  attested_calc_run_id: string;
  attestation_history_id: string;
  audit_chain_entry_id?: string | null;
}
